import { useCallback } from "react";
import { useMutation } from "@apollo/client";

import { createOffsiteTicketMutation } from "./queries";

const DEFAULT_REFETCH = ["offsiteTicketLotDashboard"];

export default function useOffsiteTicketAllocation({
  installationId,
  refetchQueries = DEFAULT_REFETCH,
} = {}) {
  const [createOffsiteTicket, { loading: allocating }] = useMutation(
    createOffsiteTicketMutation,
  );

  const allocateTicket = useCallback(
    async ({ booking, lot, ...input }) => {
      if (!booking) {
        window.alert("Booking is required to create an offsite ticket.");
        return null;
      }

      if (!lot) {
        window.alert("Booking lot is required to create an offsite ticket.");
        return null;
      }

      try {
        const { data } = await createOffsiteTicket({
          variables: {
            input: {
              installationId,
              bookingId: booking.booking?.id || booking.id || booking.ref,
              lotId: lot.id,
              guestDoesNotKnowPlateYet: Boolean(input.guestDoesNotKnowPlateYet),
              licensePlate: input.plateUnknown ? null : input.plate || null,
              guestName: booking.booking?.guestName || booking.guestName || null,
              guestEmail: booking.booking?.guestEmail || booking.email || null,
              guestPhone: booking.booking?.guestPhone || booking.phone || null,
              checkInAt: input.checkIn,
              checkOutAt: input.checkOut,
              issuedById: input.issuedBy,
              notes: input.note || null,
            },
          },
          refetchQueries,
          awaitRefetchQueries: refetchQueries.length > 0,
        });

        return data?.createOffsiteTicket ?? null;
      } catch (err) {
        window.alert(err.message || "Failed to allocate offsite ticket.");
        throw err;
      }
    },
    [createOffsiteTicket, installationId, refetchQueries],
  );

  return { allocateTicket, allocating };
}
